/**
 * Syto Metrics - Collector Service
 *
 * Central service for recording transform metrics.
 * Handles metric creation, storage, and optional console logging.
 */

import { TransformMetric, MetricInput, MetricShape, MetricMetadata } from './types';
import { addMetric, enforceRetention, loadMetricsConfig, loadMetrics } from './metrics-storage';

const IS_TEST = import.meta.env.MODE === 'test';

/**
 * Generate a UUID v4
 */
function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get data shape from various data formats
 */
export function getDataShape(data: any): { rows: number; cols: number } {
  if (!data) return { rows: 0, cols: 0 };

  // Arquero table
  if (typeof data.numRows === 'function') {
    return { rows: data.numRows(), cols: data.numCols() };
  }

  // Array of objects
  if (Array.isArray(data)) {
    return {
      rows: data.length,
      cols: data.length > 0 ? Object.keys(data[0] || {}).length : 0,
    };
  }

  return { rows: 0, cols: 0 };
}

/**
 * Format duration icon for console logging
 */
function getDurationIcon(ms: number): string {
  if (ms < 50) return '⚡';
  if (ms < 200) return '✓';
  if (ms < 500) return '⏱️';
  return '⚠️';
}

/**
 * Metrics Collector - singleton service
 */
export const metricsCollector = {
  /**
   * Record a transform metric
   */
  async record(input: MetricInput): Promise<void> {
    // No IndexedDB available, no console noise wanted
    if (IS_TEST) return;

    const config = loadMetricsConfig();

    const inputRows = input.inputRows ?? 0;
    const inputCols = input.inputCols ?? 0;
    const outputRows = input.outputRows ?? 0;
    const outputCols = input.outputCols ?? 0;

    if (config.consoleLogging) {
      this.logToConsole(input, inputRows, inputCols, outputRows, outputCols);
    }

    if (!config.enabled) {
      return;
    }

    const shape: MetricShape = {
      inputRows,
      inputCols,
      outputRows,
      outputCols,
      rowDelta: outputRows - inputRows,
      colDelta: outputCols - inputCols,
      rowsPerMs:
        inputRows > 0 && input.durationMs > 0
          ? Math.round((inputRows / input.durationMs) * 100) / 100
          : 0,
    };

    const metric: TransformMetric = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      transformType: input.transformType,
      durationMs: Math.round(input.durationMs * 100) / 100, // Round to 2 decimals
      success: input.success,
      shape: JSON.stringify(shape),
      metadata: input.metadata ? JSON.stringify(input.metadata) : null,
    };

    try {
      await addMetric(metric);
      // Enforce retention asynchronously (don't block)
      enforceRetention(config.maxRecords).catch((err) => {
        console.warn('Failed to enforce metrics retention:', err);
      });
    } catch (error) {
      console.warn('Failed to record metric:', error);
    }
  },

  /**
   * Log metric to console with duration icon and optional shape info.
   */
  logToConsole(
    input: MetricInput,
    inputRows = input.inputRows ?? 0,
    inputCols = input.inputCols ?? 0,
    outputRows = input.outputRows ?? 0,
    outputCols = input.outputCols ?? 0
  ): void {
    const icon = getDurationIcon(input.durationMs);
    const hasShape = inputRows > 0 || outputRows > 0;
    const shapeStr = hasShape
      ? `\n  ${inputRows.toLocaleString()}×${inputCols} → ${outputRows.toLocaleString()}×${outputCols}`
      : '';

    if (input.success) {
      console.log(`${icon} ${input.transformType} — ${input.durationMs.toFixed(1)}ms${shapeStr}`);
    } else {
      const errorMsg = input.metadata?.errorMessage || 'Unknown error';
      console.error(
        `❌ ${input.transformType} — ${input.durationMs.toFixed(1)}ms — FAILED${shapeStr}`,
        `\n  Error: ${errorMsg}`
      );
    }
  },

  /**
   * Time a non-tabular operation (no input/output shape tracking).
   * Recording is fire-and-forget — callers are never blocked on IndexedDB writes.
   */
  async time<T>(
    operation: string,
    fn: () => T | Promise<T>,
    metadata?: MetricMetadata
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      this.record({
        transformType: operation,
        durationMs: performance.now() - start,
        success: true,
        metadata,
      });
      return result;
    } catch (error: any) {
      this.record({
        transformType: operation,
        durationMs: performance.now() - start,
        success: false,
        metadata: { ...metadata, errorMessage: error.message },
      });
      throw error;
    }
  },

  /**
   * Get all recorded metrics (for virtual source)
   */
  async getAll(): Promise<TransformMetric[]> {
    return loadMetrics();
  },

  /**
   * Check if metrics collection is enabled
   */
  isEnabled(): boolean {
    return loadMetricsConfig().enabled;
  },

  /**
   * Check if console logging is enabled
   */
  isConsoleLoggingEnabled(): boolean {
    return loadMetricsConfig().consoleLogging;
  },
};
